import React, { useState, useEffect } from 'react';
import {
  Card, Form, Row, Col, Button, Alert, Badge, Table, Modal,
} from 'react-bootstrap';
import {
  FaPlus, FaTrash, FaBan, FaCheckCircle, FaInfoCircle, FaExclamationTriangle,
} from 'react-icons/fa';
import axios from 'axios';

/*
 * GDPR Consent Management — opt-IN register.
 * Records consent (Art 6/7), supports withdrawal (Art 7(3)), and keeps the
 * evidence needed to demonstrate consent (Art 7(1)).
 *
 * TODO: replace the PLACEHOLDER endpoints below with your real GDPR consent
 * API (same API-Gateway pattern as your other modules). ACTIVITIES posts to
 * your GDPR activity logger — set it to whatever GdprDsarManagementTab uses.
 */
const API = {
  list: 'https://REPLACE-ME.execute-api.ap-southeast-2.amazonaws.com/prod/api/gdpr/consent/list',
  create: 'https://REPLACE-ME.execute-api.ap-southeast-2.amazonaws.com/prod/api/gdpr/consent/create',
  update: (id) =>
    `https://REPLACE-ME.execute-api.ap-southeast-2.amazonaws.com/prod/api/gdpr/consent/update/${id}`,
  remove: (id) =>
    `https://REPLACE-ME.execute-api.ap-southeast-2.amazonaws.com/prod/api/gdpr/consent/delete/${id}`,
  activities:
    'https://REPLACE-ME.execute-api.ap-southeast-2.amazonaws.com/prod/api/gdpr/activities/create',
};

const LAWFUL_BASES = [
  'Consent — Art 6(1)(a)',
  'Contract — Art 6(1)(b)',
  'Legal obligation — Art 6(1)(c)',
  'Vital interests — Art 6(1)(d)',
  'Public task — Art 6(1)(e)',
  'Legitimate interests — Art 6(1)(f)',
];

const PURPOSES = [
  'Marketing communications',
  'Analytics & profiling',
  'Service provision',
  'Personalization',
  'Third-party sharing',
  'Cookies & tracking',
];

const COLLECTION_METHODS = [
  'Web form',
  'In-app',
  'Email',
  'Paper form',
  'Recorded verbal',
  'Imported / migrated',
];

const STATUS_VARIANT = {
  Given: 'success',
  Withdrawn: 'secondary',
  Pending: 'warning',
  Expired: 'danger',
};

// inline sub-text style — immune to global CSS that collapses Bootstrap margins
const subTextStyle = {
  fontSize: '12.5px',
  color: '#6c757d',
  marginLeft: '1.6rem',
  marginTop: '2px',
  lineHeight: 1.45,
};

const GdprConsentManagementTab = ({
  userData,
  onScoreUpdate,
  showNotification,
  region,
  refreshActivities,
}) => {
  const headers = {
    Region: region,
    UserId: userData?.['cognito:username'],
  };

  const [records, setRecords] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const blankConsent = {
    dataSubject: '',
    purposes: [],
    lawfulBasis: 'Consent — Art 6(1)(a)',
    status: 'Given',
    collectionMethod: 'Web form',
    noticeVersion: '',
    childToggle: false,
    childAgeBand: '13-15',
    parentalConsent: false,
    notes: '',
  };
  const [form, setForm] = useState(blankConsent);
  const [errors, setErrors] = useState({});

  const set = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const togglePurpose = (p) =>
    setForm((f) => ({
      ...f,
      purposes: f.purposes.includes(p)
        ? f.purposes.filter((x) => x !== p)
        : [...f.purposes, p],
    }));

  useEffect(() => {
    fetchRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logActivity = async (message, badge = 'info') => {
    try {
      await axios.post(
        API.activities,
        { action: badge, module: 'Consent Management', description: message },
        { headers }
      );
      if (refreshActivities) await refreshActivities();
    } catch (err) {
      console.error('❌ Error logging activity:', err);
    }
  };

  const calculateScore = (rows) => {
    if (!rows.length) {
      if (onScoreUpdate) onScoreUpdate(0);
      return;
    }
    // a record is "well-formed" if it has a basis, a notice version, and at
    // least one specific purpose (GDPR consent must be specific + demonstrable)
    const wellFormed = rows.filter(
      (r) =>
        r.lawfulBasis &&
        r.noticeVersion &&
        Array.isArray(r.purposes) &&
        r.purposes.length > 0
    ).length;
    const active = rows.filter((r) => r.status === 'Given').length;
    const completeness = (wellFormed / rows.length) * 70;
    const activeRate = (active / rows.length) * 30;
    if (onScoreUpdate) onScoreUpdate(Math.round(completeness + activeRate));
  };

  const fetchRecords = async () => {
    try {
      const res = await axios.get(API.list, { headers });
      const rows = Array.isArray(res.data) ? res.data : [];
      setRecords(rows);
      calculateScore(rows);
    } catch (error) {
      console.error('Error fetching consent records:', error);
    }
  };

  const validate = () => {
    const e = {};
    if (!form.dataSubject.trim())
      e.dataSubject = 'Enter the data subject (email or ID).';
    if (!form.purposes.length)
      e.purposes = 'Select at least one specific purpose — consent must be granular.';
    if (!form.noticeVersion.trim())
      e.noticeVersion = 'Record which privacy notice version was shown.';
    if (form.childToggle && !form.parentalConsent && form.childAgeBand === 'under13')
      e.parental = 'Parental/guardian consent is required for under-13s (Art 8).';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    const payload = {
      dataSubject: form.dataSubject.trim(),
      purposes: form.purposes,
      lawfulBasis: form.lawfulBasis,
      status: form.status,
      collectionMethod: form.collectionMethod,
      noticeVersion: form.noticeVersion.trim(),
      child: form.childToggle
        ? { ageBand: form.childAgeBand, parentalConsent: form.parentalConsent }
        : null,
      notes: form.notes.trim(),
      capturedAt: new Date().toISOString(),
    };
    setSubmitting(true);
    try {
      await axios.post(API.create, payload, { headers });
      await logActivity(
        `Recorded GDPR consent for ${payload.dataSubject} (${form.lawfulBasis})`,
        'success'
      );
      showNotification('Consent record saved!');
      setForm(blankConsent);
      setErrors({});
      setShowModal(false);
      fetchRecords();
    } catch (error) {
      console.error('Error saving consent:', error);
      showNotification('Error saving consent record', 'danger');
    } finally {
      setSubmitting(false);
    }
  };

  const withdrawConsent = async (id, subject) => {
    if (!window.confirm(`Withdraw consent for ${subject}?`)) return;
    try {
      await axios.put(
        API.update(id),
        { status: 'Withdrawn', withdrawnAt: new Date().toISOString() },
        { headers }
      );
      await logActivity(`Withdrew GDPR consent for ${subject}`, 'warning');
      showNotification('Consent withdrawn.');
      fetchRecords();
    } catch (error) {
      console.error('Error withdrawing consent:', error);
      showNotification('Error withdrawing consent', 'danger');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this consent record?')) return;
    try {
      await axios.delete(API.remove(id), { headers });
      await logActivity('Deleted GDPR consent record', 'warning');
      showNotification('Record deleted.');
      fetchRecords();
    } catch (error) {
      console.error('Error deleting record:', error);
      showNotification('Error deleting record', 'danger');
    }
  };

  const purposesText = (p) => (Array.isArray(p) ? p.join(', ') : p || '—');

  return (
    <>
      <Alert variant="info" className="d-flex">
        <FaInfoCircle className="me-2 mt-1 flex-shrink-0" />
        <div>
          <strong>GDPR consent</strong> must be freely given, specific, informed
          and unambiguous (Art 4(11)). You must be able to demonstrate it
          (Art 7(1)), and withdrawal must be as easy as giving it (Art 7(3)).
        </div>
      </Alert>

      <Row className="mb-3">
        <Col>
          <Button variant="primary" onClick={() => setShowModal(true)}>
            <FaPlus /> Record Consent
          </Button>
        </Col>
      </Row>

      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>Data Subject</th>
            <th>Purposes</th>
            <th>Lawful Basis</th>
            <th>Collected Via</th>
            <th>Notice</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {records.length === 0 ? (
            <tr>
              <td colSpan={7} className="text-center text-muted">
                No consent records yet.
              </td>
            </tr>
          ) : (
            records.map((r) => (
              <tr key={r.id || r.consentId}>
                <td>{r.dataSubject}</td>
                <td className="small">{purposesText(r.purposes)}</td>
                <td className="small">{r.lawfulBasis}</td>
                <td className="small">{r.collectionMethod}</td>
                <td className="small">{r.noticeVersion}</td>
                <td>
                  <Badge bg={STATUS_VARIANT[r.status] || 'secondary'}>
                    {r.status}
                  </Badge>
                </td>
                <td>
                  {r.status === 'Given' && (
                    <Button
                      size="sm"
                      variant="outline-warning"
                      className="me-1"
                      title="Withdraw consent"
                      onClick={() =>
                        withdrawConsent(r.id || r.consentId, r.dataSubject)
                      }
                    >
                      <FaBan />
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline-danger"
                    onClick={() => handleDelete(r.id || r.consentId)}
                  >
                    <FaTrash />
                  </Button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>

      {/* Record Consent modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Record GDPR Consent</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>
                Data subject <span className="text-primary">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                value={form.dataSubject}
                isInvalid={!!errors.dataSubject}
                placeholder="Email or subject ID"
                onChange={(e) => set('dataSubject', e.target.value)}
              />
              <Form.Control.Feedback type="invalid">
                {errors.dataSubject}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                Purposes <span className="text-primary">*</span>{' '}
                <span className="text-muted small">(granular — pick each that applies)</span>
              </Form.Label>
              <Row>
                {PURPOSES.map((p) => (
                  <Col md={6} key={p}>
                    <Form.Check
                      type="checkbox"
                      id={`purpose-${p}`}
                      label={p}
                      checked={form.purposes.includes(p)}
                      onChange={() => togglePurpose(p)}
                    />
                  </Col>
                ))}
              </Row>
              {errors.purposes && (
                <div className="text-danger small mt-1">{errors.purposes}</div>
              )}
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Lawful basis</Form.Label>
                  <Form.Select
                    value={form.lawfulBasis}
                    onChange={(e) => set('lawfulBasis', e.target.value)}
                  >
                    {LAWFUL_BASES.map((b) => (
                      <option key={b} value={b}>
                        {b}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Collected via</Form.Label>
                  <Form.Select
                    value={form.collectionMethod}
                    onChange={(e) => set('collectionMethod', e.target.value)}
                  >
                    {COLLECTION_METHODS.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Privacy notice version <span className="text-primary">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={form.noticeVersion}
                    isInvalid={!!errors.noticeVersion}
                    placeholder="e.g., Privacy Notice v4.1 (2026-01)"
                    onChange={(e) => set('noticeVersion', e.target.value)}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.noticeVersion}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={form.status}
                    onChange={(e) => set('status', e.target.value)}
                  >
                    <option value="Given">Given</option>
                    <option value="Pending">Pending</option>
                    <option value="Withdrawn">Withdrawn</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <div style={{ marginBottom: '8px' }}>
              <Form.Check
                type="checkbox"
                id="gdprChild"
                checked={form.childToggle}
                onChange={(e) => set('childToggle', e.target.checked)}
                label="Data subject is a child (Art 8)"
              />
              <div style={subTextStyle}>
                Information-society services need parental consent below the
                digital-consent age (13–16, set by member state).
              </div>
            </div>
            {form.childToggle && (
              <div className="border rounded p-3 mb-2">
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-2" style={{ maxWidth: 320 }}>
                      <Form.Label>Age band</Form.Label>
                      <Form.Select
                        value={form.childAgeBand}
                        onChange={(e) => set('childAgeBand', e.target.value)}
                      >
                        <option value="13-15">13–15 — member-state dependent</option>
                        <option value="under13">Under 13 — parental consent required</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
                <Form.Check
                  type="checkbox"
                  id="gdprParental"
                  checked={form.parentalConsent}
                  onChange={(e) => set('parentalConsent', e.target.checked)}
                  label="Parental/guardian consent obtained and verifiable"
                />
                {errors.parental && (
                  <div className="text-danger small mt-1">{errors.parental}</div>
                )}
              </div>
            )}

            <Form.Group className="mb-1" style={{ marginTop: '16px' }}>
              <Form.Label>Notes / evidence reference</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={form.notes}
                placeholder="e.g., link to signed form, opt-in event ID, IP/timestamp"
                onChange={(e) => set('notes', e.target.value)}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={submitting}>
            {submitting ? 'Saving…' : 'Save Consent'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default GdprConsentManagementTab;